/**
 * Presentación de importes, cantidades y fechas.
 *
 * El backend envía dinero en céntimos enteros y cantidades en milésimas: la
 * conversión a decimal ocurre exclusivamente aquí, al pintar.
 */

const cache = new Map();

function formatter(key, factory) {
  if (!cache.has(key)) cache.set(key, factory());
  return cache.get(key);
}

export function formatMoney(cents, { locale = "es", currency = "EUR" } = {}) {
  const value = Number.isFinite(cents) ? cents : 0;
  return formatter(
    `money:${locale}:${currency}`,
    () => new Intl.NumberFormat(locale, { style: "currency", currency }),
  ).format(value / 100);
}

export function formatQuantity(quantityMilli, locale = "es") {
  // An unresolved documentary quantity is absent, not a measured zero.
  if (!Number.isFinite(quantityMilli)) return "—";
  return formatter(
    `qty:${locale}`,
    () => new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }),
  ).format(quantityMilli / 1000);
}

export function parseQuantityToMilli(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0
      ? Math.round(value * 1000)
      : null;
  }
  const normalised = String(value ?? "")
    .trim()
    .replace(",", ".");
  if (!normalised) return null;
  const parsed = Number(normalised);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 1000);
}

/** Valor editable de una cantidad, con coma decimal en español. */
export function quantityToInput(quantityMilli, locale = "es") {
  if (!Number.isFinite(quantityMilli)) return "";
  const value = quantityMilli / 1000;
  return locale === "en" ? String(value) : String(value).replace(".", ",");
}

export function formatDate(value, locale = "es") {
  if (!value) return "—";
  return formatter(
    `date:${locale}`,
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
  ).format(new Date(value));
}

export function formatDateLong(value, locale = "es") {
  if (!value) return "—";
  return formatter(
    `dateLong:${locale}`,
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
  ).format(new Date(value));
}

/**
 * Solo la hora.
 *
 * En un chat la fecha completa en cada burbuja sobra: el día lo marca el
 * separador que abre cada jornada.
 */
export function formatTime(value, locale = "es") {
  if (!value) return "";
  return formatter(
    `time:${locale}`,
    () =>
      new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }),
  ).format(new Date(value));
}

/** Día local de una marca de tiempo, para agrupar mensajes por jornada. */
export function dayKeyOf(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function formatDateTime(value, locale = "es") {
  if (!value) return "—";
  return formatter(
    `dateTime:${locale}`,
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
  ).format(new Date(value));
}

/** Valor para un `<input type="date">`, que siempre espera AAAA-MM-DD. */
export function toDateInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Hoy en el calendario de quien usa el portal, en formato de `<input type="date">`. */
export function todayInputValue() {
  return toDateInputValue(new Date());
}

/** Una fecha `AAAA-MM-DD` anterior a hoy (las cadenas ISO se comparan como fechas). */
export function isPastDateInput(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value ?? "") && value < todayInputValue();
}

export function formatFileSize(bytes, locale = "es") {
  if (!Number.isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) {
    return `${new Intl.NumberFormat(locale).format(Math.round(bytes / 1024))} KB`;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    bytes / (1024 * 1024),
  )} MB`;
}

/** Iniciales del avatar. Nunca devuelve cadena vacía. */
export function initialsOf(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase() || "?";
}
